import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { z } from 'zod';
import { LEAD_STAGES, OPEN_LEAD_STAGES, setLeadStage, type LeadStage } from '@mkcore/crm/schema';
import { Button, Card, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, Tabs, TabsList, TabsTrigger, cn } from '@mkcore/ui';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { formatDate } from '@/lib/format';
import { DataTable, type ColumnDef } from '@/components/data-table';
import { LeadDialog, StageBadge, leadCompany, leadsQuery, money, type Lead } from '@/features/crm/leads';
import { CrmOverview } from '@/features/overviews';

const search = z.object({ view: z.enum(['board', 'list']).optional(), q: z.string().optional(), mine: z.boolean().optional(), closed: z.boolean().optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/crm/leads/')({ validateSearch: search, component: Leads });

/**
 * The pipeline (CO-0582): open leads in a column per stage with their value and weighted value, or all leads in a
 * list. Dragging a card, or choosing a stage in its menu, moves the lead; the chance follows the stage.
 */
function Leads() {
  const { t, i18n } = useTranslation();
  const s = Route.useSearch();
  const me = useMe();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const canCreate = useCan('crm:create');
  const canUpdate = useCan('crm:update');
  const view = s.view ?? 'board';
  const [creating, setCreating] = useState(false);
  const q = useInfiniteQuery(leadsQuery({ q: s.q || undefined, ownerId: s.mine ? me.id : undefined, open: view === 'board' || !s.closed ? true : undefined, limit: 200 }));
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const totals = q.data?.pages[0]?.totals ?? [];
  const locale = i18n.language === 'en' ? 'en' : 'nl';
  const open = totals.filter((x) => (OPEN_LEAD_STAGES as readonly string[]).includes(x.stage));
  const sum = (k: 'valueCents' | 'weightedCents') => open.reduce((a, x) => a + x[k], 0);

  return (
    <>
      <PageHeader
        eyebrow={t('nav.crm')}
        title={t('leads.title')}
        description={t('leads.description')}
        actions={
          canCreate && (
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('leads.new')}
            </Button>
          )
        }
      />
      <CrmOverview />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Tabs value={view} onValueChange={(v) => set({ view: v as Search['view'] })}>
          <TabsList aria-label={t('leads.view')}>
            <TabsTrigger value="board">{t('leads.board')}</TabsTrigger>
            <TabsTrigger value="list">{t('leads.list')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <Field label={t('common.search')} className="w-64">
          {(p) => <Input {...p} value={s.q ?? ''} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('leads.owner')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.mine ? 'mine' : ''} onChange={(e) => set({ mine: e.target.value === 'mine' || undefined })}>
              <option value="">{t('common.all')}</option>
              <option value="mine">{t('leads.mine')}</option>
            </NativeSelect>
          )}
        </Field>
        {view === 'list' && (
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input type="checkbox" checked={!!s.closed} onChange={(e) => set({ closed: e.target.checked || undefined })} /> {t('leads.includeClosed')}
          </label>
        )}
        <p className="ml-auto pb-2 text-sm text-fg-2" data-testid="pipeline-totals">
          {t('leads.pipelineTotal', { value: money(sum('valueCents'), 'EUR', locale), weighted: money(sum('weightedCents'), 'EUR', locale) })}
        </p>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-96" />
      ) : !rows.length ? (
        <EmptyState title={t('leads.empty')} description={canCreate ? t('leads.emptyHint') : undefined} />
      ) : view === 'board' ? (
        <PipelineBoard leads={rows} totals={totals} movable={canUpdate} locale={locale} />
      ) : (
        <LeadList leads={rows} locale={locale} />
      )}
      {creating && <LeadDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function PipelineBoard({ leads, totals, movable, locale }: { leads: Lead[]; totals: { stage: LeadStage; count: number; weightedCents: number }[]; movable: boolean; locale: 'nl' | 'en' }) {
  const { t } = useTranslation();
  const move = useActionMutation(setLeadStage, { invalidate: [['crm']] });
  const [over, setOver] = useState<string | null>(null);
  const drop = (id: string, stage: LeadStage) => {
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === stage || !(OPEN_LEAD_STAGES as readonly string[]).includes(stage)) return;
    move.mutate({ input: { id, stage: stage as (typeof OPEN_LEAD_STAGES)[number] } });
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" role="list" aria-label={t('leads.board')}>
      {OPEN_LEAD_STAGES.map((stage) => {
        const cards = leads.filter((l) => l.stage === stage);
        const total = totals.find((x) => x.stage === stage);
        return (
          <section
            key={stage}
            role="listitem"
            aria-label={t('leads.column', { stage: t(`leads.stage.${stage}`), count: cards.length })}
            className={cn('flex w-72 shrink-0 flex-col gap-2 rounded-lg bg-sunken p-2', over === stage && 'ring-2 ring-gold')}
            onDragOver={(e) => {
              if (!movable) return;
              e.preventDefault();
              setOver(stage);
            }}
            onDragLeave={() => setOver((o) => (o === stage ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              const id = e.dataTransfer.getData('text/x-lead');
              if (id) drop(id, stage);
            }}
          >
            <h2 className="flex items-baseline justify-between px-1 text-sm font-bold">
              <span>{t(`leads.stage.${stage}`)}</span>
              <span className="text-xs font-normal text-fg-3 tabular">{money(total?.weightedCents ?? 0, 'EUR', locale)}</span>
            </h2>
            {cards.map((l) => (
              <Card
                key={l.id}
                draggable={movable}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/x-lead', l.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                className={cn('grid gap-1 p-2 text-sm', movable && 'cursor-grab', move.isPending && move.variables?.input.id === l.id && 'opacity-50')}
              >
                <Link to="/crm/leads/$id" params={{ id: l.id }} className="font-bold hover:underline">
                  {l.title}
                </Link>
                <div className="text-xs text-fg-2">{leadCompany(l)}</div>
                <div className="flex flex-wrap justify-between gap-1 text-xs text-fg-3">
                  <span className="tabular">
                    {money(l.expectedValueCents, l.currency, locale)} · {l.probability}%
                  </span>
                  {l.openFollowUps > 0 && <span>{t('leads.followUps', { count: l.openFollowUps, at: l.nextFollowUpOn ? formatDate(l.nextFollowUpOn) : '–' })}</span>}
                </div>
                {movable && (
                  <NativeSelect aria-label={t('leads.moveTo', { title: l.title })} className="h-7 text-xs" value={l.stage} disabled={move.isPending} onChange={(e) => drop(l.id, e.target.value as LeadStage)}>
                    {OPEN_LEAD_STAGES.map((st) => (
                      <option key={st} value={st}>
                        {t(`leads.stage.${st}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Card>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function LeadList({ leads, locale }: { leads: Lead[]; locale: 'nl' | 'en' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<Lead>[]>(
    () => [
      {
        id: 'title',
        label: t('leads.fields.title'),
        value: (l) => l.title,
        cell: (l) => (
          <Link to="/crm/leads/$id" params={{ id: l.id }} className="font-bold hover:underline">
            {l.title}
          </Link>
        ),
      },
      { id: 'company', label: t('leads.fields.organisation'), groupable: true, value: (l) => leadCompany(l), cell: (l) => leadCompany(l) },
      {
        id: 'stage',
        label: t('leads.fields.stage'),
        type: 'enum',
        options: LEAD_STAGES.map((st) => ({ value: st, label: t(`leads.stage.${st}`) })),
        groupable: true,
        value: (l) => l.stage,
        cell: (l) => <StageBadge stage={l.stage} />,
      },
      { id: 'value', label: t('leads.fields.value'), type: 'number', value: (l) => l.expectedValueCents, cell: (l) => money(l.expectedValueCents, l.currency, locale), className: 'text-right tabular' },
      { id: 'weighted', label: t('leads.fields.weighted'), type: 'number', value: (l) => l.weightedValueCents, cell: (l) => money(l.weightedValueCents, l.currency, locale), className: 'text-right tabular' },
      { id: 'probability', label: t('leads.fields.probability'), type: 'number', hidden: true, value: (l) => l.probability, cell: (l) => `${l.probability}%`, className: 'text-right tabular' },
      { id: 'owner', label: t('leads.owner'), groupable: true, value: (l) => l.ownerName, cell: (l) => l.ownerName ?? '–', className: 'hidden md:table-cell' },
      { id: 'source', label: t('leads.fields.source'), groupable: true, hidden: true, value: (l) => l.source, cell: (l) => l.source ?? '–' },
      { id: 'close', label: t('leads.fields.expectedCloseOn'), type: 'date', value: (l) => l.expectedCloseOn, cell: (l) => (l.expectedCloseOn ? formatDate(l.expectedCloseOn) : '–'), className: 'hidden lg:table-cell' },
      { id: 'createdAt', label: t('common.created'), type: 'date', hidden: true, value: (l) => l.createdAt, cell: (l) => formatDate(l.createdAt) },
    ],
    [t, locale],
  );
  return <DataTable listKey="crm.lead.list" columns={columns} rows={leads} rowKey={(l) => l.id} onOpen={(l) => void navigate({ to: '/crm/leads/$id', params: { id: l.id } })} />;
}
